import { Form } from "@unform/web";
import { Building } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import DatePicker from "~/components/RegisterForm/DatePicker";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { AlertContext } from "~/App";
import { inactiveReasonsOptions } from "~/functions/selectPopoverOptions";
import { format, parseISO } from "date-fns";
import FindGeneric from "~/components/Finds/FindGeneric";

export const InputContext = createContext({});

export default function Activate({
  access,
  id,
  defaultFormType = "preview",
  selected,
  handleOpened,
}) {
  const { alertBox } = useContext(AlertContext);
  const { successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    loaded: false,
    bank_name: "",
    bank_alias: "",
    loaded: true,
    installment_amount: 0,
  });

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleGeneralFormSubmit(data) {
    const { date = null, studentgroup = null } = data;
    if (!date || !studentgroup) {
      return;
    }
    try {
      await api.post(`/students/activate/${selected[0].student_id}`, {
        studentgroup,
        date: format(date, "yyyy-MM-dd"),
      });
      toast("Student Activated!", { autoClose: 1000 });
      alertBox({
        title: "Attention!",
        descriptionHTML: "Do you want to send the student a welcome letter?",
        buttons: [
          {
            title: "Yes",
            onPress: async () => {
              await api.post(`/send-welcome-letter/${selected[0].id}`, {
                studentgroup,
                date: format(date, "yyyy-MM-dd"),
              });
            },
          },
          {
            title: "No",
            class: "cancel",
          },
        ],
      });
      handleOpened(null);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    }
  }

  useEffect(() => {
    async function loadData() {
      const { data } = await api.get(`/students/${selected[0].student_id}`);
      setPageData({ ...data, loaded: true });
    }
    loadData();
  }, []);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <Building size={16} /> General
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form
                ref={generalForm}
                onSubmit={handleGeneralFormSubmit}
                className="w-full"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  {id === "new" || pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title={
                          "Activation - " +
                          pageData.name +
                          " " +
                          pageData.last_name
                        }
                        registry={registry}
                        InputContext={InputContext}
                      />

                      <InputLineGroup
                        title="GENERAL"
                        activeMenu={activeMenu === "general"}
                      >
                        <FindGeneric
                          route="studentgroups/ongoing-only"
                          title="Student Group"
                          scope="studentgroup"
                          required
                          type={`null&student_id=${selected?.[0]?.student_id || ""}`}
                          InputContext={InputContext}
                          defaultValue={{
                            id: pageData.studentgroup?.id,
                            name: pageData.studentgroup?.name,
                          }}
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                            {
                              title: "Status",
                              name: "status",
                            },
                          ]}
                        />
                        <InputLine title="Details">
                          <DatePicker
                            name="date"
                            grow
                            required
                            title="Date"
                            defaultValue={pageData.date}
                            placeholderText="MM/DD/YYYY"
                            InputContext={InputContext}
                          />
                        </InputLine>
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
